import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import QuizView from "@/components/QuizView";
import { Check, PlayCircle, Clock, Award, FileQuestion } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function CoursePlayer() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [owned, setOwned] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [active, setActive] = useState(null); // { kind: 'lesson'|'quiz', data, quizKind? }
  const [bestByQuizId, setBestByQuizId] = useState({});

  useEffect(() => {
    (async () => {
      const [pr, ow] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/library/owns/${id}`).catch(() => ({ data: { owned: false } })),
      ]);
      setCourse(pr.data);
      setOwned(ow.data.owned);
      if (ow.data.owned) {
        const [pg, qr] = await Promise.all([
          api.get(`/progress/${id}`),
          api.get(`/courses/${id}/quiz-results`).catch(() => ({ data: [] })),
        ]);
        setCompleted(pg.data.completed_lessons || []);
        const map = {};
        for (const r of qr.data) map[r.quiz_id] = r;
        setBestByQuizId(map);
      }
      const firstLesson = pr.data.modules?.[0]?.lessons?.[0];
      if (firstLesson) setActive({ kind: "lesson", data: firstLesson });
    })();
  }, [id]);

  const allLessons = useMemo(
    () => (course?.modules || []).flatMap((m) => m.lessons || []),
    [course],
  );
  const progressPct = allLessons.length
    ? Math.round((completed.length / allLessons.length) * 100)
    : 0;

  if (owned === false) return <Navigate to={`/p/${id}`} replace />;
  if (!course || owned === null) {
    return <Layout><div className="container-narrow py-32 overline text-brand-inkMuted">Loading…</div></Layout>;
  }

  const toggle = async (lessonId) => {
    const { data } = await api.post(`/progress/${id}/toggle/${lessonId}`);
    setCompleted(data.completed_lessons);
  };

  const onQuizCompleted = (result) => {
    const qid = active?.data?.quiz_id;
    if (!qid) return;
    setBestByQuizId((prev) => {
      if (!prev[qid] || result.score > prev[qid].score) {
        return { ...prev, [qid]: { quiz_id: qid, score: result.score, passed: result.passed } };
      }
      return prev;
    });
  };

  return (
    <Layout hideFooter>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-[calc(100vh-5rem)]">
        <aside className="border-r border-brand-line bg-white">
          <div className="p-6 border-b border-brand-line">
            <Link to="/library" className="overline text-brand-inkMuted hover:text-brand-ink" data-testid="back-to-library">← My Library</Link>
            <h2 className="font-display text-2xl mt-3 text-brand-ink leading-snug">{course.title}</h2>
            <p className="text-sm text-brand-inkMuted mt-1">{course.instructor}</p>
            <div className="mt-5">
              <div className="flex items-center justify-between overline text-brand-inkMuted">
                <span>Progress</span><span>{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="mt-2 h-1 bg-brand-line" />
            </div>
          </div>
          <nav className="p-4 space-y-6 max-h-[calc(100vh-15rem)] overflow-y-auto">
            {course.modules.map((m, mi) => (
              <div key={m.module_id || mi}>
                <p className="overline text-brand-inkMuted px-2 mb-2">Module 0{mi + 1}</p>
                <p className="font-serif text-base text-brand-ink px-2 mb-3">{m.title}</p>
                <ul className="space-y-1">
                  {m.lessons.map((l) => {
                    const done = completed.includes(l.lesson_id);
                    const isActive = active?.kind === "lesson" && active.data.lesson_id === l.lesson_id;
                    return (
                      <li key={l.lesson_id}>
                        <button
                          data-testid={`lesson-btn-${l.lesson_id}`}
                          onClick={() => setActive({ kind: "lesson", data: l })}
                          className={`w-full text-left px-2 py-2 text-sm flex items-start gap-3 transition-colors ${
                            isActive ? "bg-brand-ink text-white" : "text-brand-ink hover:bg-brand-bg"
                          }`}
                        >
                          <span className={`mt-0.5 w-4 h-4 border flex items-center justify-center flex-none ${
                            done ? "bg-brand-rust border-brand-rust" : isActive ? "border-white/40" : "border-brand-line"
                          }`}>
                            {done && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="flex-1">
                            <span className="block leading-snug">{l.title}</span>
                            <span className={`overline mt-1 inline-flex items-center gap-1 ${isActive ? "text-white/60" : "text-brand-inkMuted"}`}>
                              <Clock className="w-3 h-3" /> {l.duration}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {m.quiz && (
                    <li>
                      <button
                        data-testid={`quiz-btn-${m.quiz.quiz_id}`}
                        onClick={() => setActive({ kind: "quiz", data: m.quiz, quizKind: "module" })}
                        className={`w-full text-left px-2 py-2 text-sm flex items-start gap-3 transition-colors ${
                          active?.kind === "quiz" && active.data.quiz_id === m.quiz.quiz_id
                            ? "bg-brand-ink text-white" : "text-brand-ink hover:bg-brand-bg"
                        }`}
                      >
                        <FileQuestion className={`mt-0.5 w-4 h-4 flex-none ${bestByQuizId[m.quiz.quiz_id]?.passed ? "text-brand-rust" : "text-brand-inkMuted"}`} />
                        <span className="flex-1">
                          <span className="block leading-snug">Module quiz</span>
                          <span className="overline mt-1 inline-flex items-center gap-1 text-brand-inkMuted">
                            {bestByQuizId[m.quiz.quiz_id] ? `${bestByQuizId[m.quiz.quiz_id].score}% best${bestByQuizId[m.quiz.quiz_id].passed ? " · passed" : ""}` : `${m.quiz.questions.length} questions`}
                          </span>
                        </span>
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ))}

            {course.final_quiz && (
              <div className="pt-4 border-t border-brand-line">
                <p className="overline text-brand-rust px-2 mb-2">Final Exam</p>
                <button
                  data-testid="final-quiz-btn"
                  onClick={() => setActive({ kind: "quiz", data: course.final_quiz, quizKind: "final" })}
                  className={`w-full text-left px-2 py-2 text-sm flex items-start gap-3 transition-colors ${
                    active?.kind === "quiz" && active.data.quiz_id === course.final_quiz.quiz_id
                      ? "bg-brand-ink text-white" : "text-brand-ink hover:bg-brand-bg"
                  }`}
                >
                  <Award className={`mt-0.5 w-4 h-4 flex-none ${bestByQuizId[course.final_quiz.quiz_id]?.passed ? "text-brand-rust" : "text-brand-inkMuted"}`} />
                  <span className="flex-1">
                    <span className="block leading-snug font-medium">Take the final</span>
                    <span className="overline mt-1 inline-flex items-center gap-1 text-brand-inkMuted">
                      {bestByQuizId[course.final_quiz.quiz_id]
                        ? `${bestByQuizId[course.final_quiz.quiz_id].score}% best${bestByQuizId[course.final_quiz.quiz_id].passed ? " · passed" : ""}`
                        : `${course.final_quiz.questions.length} questions · 80% to pass`}
                    </span>
                  </span>
                </button>
                {bestByQuizId[course.final_quiz.quiz_id]?.passed && (
                  <Link
                    to={`/certificate/${id}`}
                    data-testid="sidebar-certificate-link"
                    className="mt-2 w-full text-left px-2 py-2 text-sm flex items-start gap-3 text-brand-rust hover:bg-brand-bg transition-colors"
                  >
                    <Award className="mt-0.5 w-4 h-4 flex-none" />
                    <span className="flex-1">
                      <span className="block leading-snug font-medium">View certificate</span>
                      <span className="overline mt-1 inline-flex items-center gap-1 text-brand-inkMuted">Print or save as PDF</span>
                    </span>
                  </Link>
                )}
              </div>
            )}
          </nav>
        </aside>

        <main className="bg-brand-bg">
          {active?.kind === "lesson" && (
            <div className="container-narrow py-10 max-w-3xl">
              <span className="overline text-brand-rust">Lesson</span>
              <h1 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink leading-[1.1]" data-testid="lesson-title">{active.data.title}</h1>
              <div className="mt-2 overline text-brand-inkMuted flex items-center gap-2">
                <Clock className="w-3 h-3" /> {active.data.duration}
              </div>
              <div className="mt-8 aspect-video bg-black border border-brand-line overflow-hidden">
                {active.data.video_url ? (
                  <iframe title={active.data.title} src={active.data.video_url} className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white overline opacity-60">
                    <PlayCircle className="w-8 h-8" />
                  </div>
                )}
              </div>
              {active.data.content && <p className="mt-8 text-brand-ink leading-relaxed text-lg">{active.data.content}</p>}
              <div className="mt-10 flex items-center justify-between border-t border-brand-line pt-6">
                <button
                  data-testid="toggle-complete-btn"
                  onClick={() => toggle(active.data.lesson_id)}
                  className={`px-6 py-3 text-sm font-medium tracking-wide transition-colors ${
                    completed.includes(active.data.lesson_id)
                      ? "border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white"
                      : "bg-brand-ink text-white hover:bg-black"
                  }`}
                >
                  {completed.includes(active.data.lesson_id) ? "Mark as incomplete" : "Mark as complete"}
                </button>
                <span className="overline text-brand-inkMuted">{completed.length} of {allLessons.length} done</span>
              </div>
            </div>
          )}

          {active?.kind === "quiz" && (
            <div className="container-narrow py-10">
              <QuizView courseId={id} quiz={active.data} kind={active.quizKind} onCompleted={onQuizCompleted} />
            </div>
          )}
        </main>
      </div>
    </Layout>
  );
}
